import { useEffect, useMemo, useRef, useState } from "react";
import api, { errMsg } from "@/lib/api";
import { toast } from "sonner";
import { X, Send, Loader2, Search, Users, StopCircle, CheckCircle2, AlertTriangle } from "lucide-react";

// ── Toplu tanıtım maili gönderme penceresi ──
export const BulkMailModal = ({ firms, onClose, onDone }) => {
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState("");
  const [signature, setSignature] = useState(null);
  const [withSignature, setWithSignature] = useState(true);
  const [extraNote, setExtraNote] = useState("");
  const [search, setSearch] = useState("");
  const [onlyUnmailed, setOnlyUnmailed] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [job, setJob] = useState(null); // {id, status, total, sent, failed, errors, last_firm}
  const [starting, setStarting] = useState(false);
  const pollRef = useRef(null);

  useEffect(() => {
    api.get("/email-templates")
      .then(({ data }) => { setTemplates(data); if (data[0]) setTemplateId(data[0].id); })
      .catch((e) => toast.error(errMsg(e)));
    api.get("/mail-signature").then(({ data }) => setSignature(data)).catch(() => {});
    return () => clearInterval(pollRef.current);
  }, []);

  const withEmail = useMemo(() => firms.filter((f) => (f.email || "").trim().includes("@")), [firms]);
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return withEmail.filter((f) => {
      if (onlyUnmailed && (f.sent_emails || []).length > 0) return false;
      if (!q) return true;
      return `${f.name} ${f.email} ${f.contact_person || ""} ${f.district || ""} ${f.city || ""}`.toLowerCase().includes(q);
    });
  }, [withEmail, search, onlyUnmailed]);

  const allFilteredSelected = filtered.length > 0 && filtered.every((f) => selected.has(f.id));
  const toggleAll = () => {
    const next = new Set(selected);
    if (allFilteredSelected) filtered.forEach((f) => next.delete(f.id));
    else filtered.forEach((f) => next.add(f.id));
    setSelected(next);
  };
  const toggleOne = (id) => {
    const next = new Set(selected);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelected(next);
  };

  const poll = (jobId) => {
    clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/bulk-mail-jobs/${jobId}`);
        setJob(data);
        if (data.status !== "running") {
          clearInterval(pollRef.current);
          onDone?.();
          if (data.status === "done") toast.success(`Toplu gönderim bitti: ${data.sent} başarılı, ${data.failed} hatalı`, { duration: 8000 });
        }
      } catch { /* geçici ağ hatası — sonraki denemede düzelir */ }
    }, 1500);
  };

  const start = async () => {
    if (!templateId) { toast.error("Taslak seçin"); return; }
    if (selected.size === 0) { toast.error("En az bir firma seçin"); return; }
    if (!window.confirm(`${selected.size} firmaya "${templates.find((t) => t.id === templateId)?.name}" taslağıyla mail gönderilecek. Onaylıyor musunuz?`)) return;
    setStarting(true);
    try {
      const { data } = await api.post("/tender-tracking/bulk-send-email", {
        template_id: templateId,
        firm_ids: [...selected],
        include_signature: withSignature && !!(signature?.has_image || signature?.disclaimer_text),
        extra_note: extraNote,
      });
      setJob({ id: data.job_id, status: "running", total: data.total, sent: 0, failed: 0, errors: [], last_firm: "" });
      poll(data.job_id);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setStarting(false); }
  };

  const cancel = async () => {
    try { await api.post(`/bulk-mail-jobs/${job.id}/cancel`); toast.info("Gönderim iptal edildi"); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const running = job?.status === "running";
  const progress = job ? Math.round(((job.sent + job.failed) / Math.max(job.total, 1)) * 100) : 0;

  return (
    <div className="fixed inset-0 bg-black/50 z-[600] flex items-start justify-center overflow-y-auto p-4" onClick={running ? undefined : onClose}>
      <div className="bg-white w-full max-w-4xl mt-8 border border-[#E5E5E5]" onClick={(e) => e.stopPropagation()} data-testid="bulk-mail-modal">
        <div className="px-5 py-4 border-b border-[#E5E5E5] flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">Pazarlama</div>
            <div className="font-bold font-heading flex items-center gap-2"><Users className="w-4 h-4 text-[#0020C2]" /> Toplu Tanıtım Maili</div>
          </div>
          <button onClick={onClose} disabled={running} className="p-1.5 hover:bg-[#F5F5F5] disabled:opacity-30" data-testid="bulk-mail-close"><X className="w-4 h-4" /></button>
        </div>

        {!job && (
          <div className="p-5 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Taslak</label>
                <select data-testid="bulk-mail-template" value={templateId} onChange={(e) => setTemplateId(e.target.value)} className="mt-1 w-full px-3 py-2 bg-white border border-[#E5E5E5] text-sm">
                  {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Ek Not (tüm maillere eklenir, opsiyonel)</label>
                <input data-testid="bulk-mail-note" value={extraNote} onChange={(e) => setExtraNote(e.target.value)} maxLength={2000}
                  placeholder="örn: Bölgenizde mevcut güzergahlarımız vardır." className="mt-1 w-full px-3 py-2 bg-white border border-[#E5E5E5] text-sm" />
              </div>
            </div>
            <div className="flex items-center gap-5 flex-wrap text-xs">
              {signature && (signature.has_image || signature.disclaimer_text) && (
                <label className="flex items-center gap-2 font-semibold cursor-pointer select-none">
                  <input type="checkbox" checked={withSignature} onChange={(e) => setWithSignature(e.target.checked)} className="w-4 h-4 accent-[#0020C2]" data-testid="bulk-mail-signature-toggle" />
                  Mail imzası ekle
                </label>
              )}
              <label className="flex items-center gap-2 font-semibold cursor-pointer select-none">
                <input type="checkbox" checked={onlyUnmailed} onChange={(e) => setOnlyUnmailed(e.target.checked)} className="w-4 h-4 accent-[#0020C2]" data-testid="bulk-mail-unmailed-toggle" />
                Sadece hiç mail atılmamış firmalar
              </label>
              <span className="text-neutral-400">Hitap otomatik: yetkili varsa "Sayın Yetkili", yoksa "Merhabalar"</span>
            </div>

            <div className="border border-[#E5E5E5]">
              <div className="px-3 py-2 border-b border-[#E5E5E5] flex items-center gap-3 flex-wrap bg-[#FAFAFA]">
                <label className="flex items-center gap-2 text-xs font-bold cursor-pointer select-none">
                  <input type="checkbox" checked={allFilteredSelected} onChange={toggleAll} className="w-4 h-4 accent-[#0020C2]" data-testid="bulk-mail-select-all" />
                  Tümünü seç ({filtered.length})
                </label>
                <div className="relative flex-1 min-w-[200px]">
                  <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-neutral-400" />
                  <input data-testid="bulk-mail-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Firma, mail, bölge ara..."
                    className="pl-8 pr-3 py-1.5 border border-[#E5E5E5] text-xs w-full bg-white" />
                </div>
                <span className="text-xs font-bold text-[#0020C2]" data-testid="bulk-mail-selected-count">{selected.size} seçili</span>
              </div>
              <div className="max-h-72 overflow-y-auto">
                {filtered.map((f) => (
                  <label key={f.id} className="flex items-center gap-2.5 px-3 py-1.5 border-t border-[#F5F5F5] text-xs hover:bg-[#FAFAFA] cursor-pointer" data-testid={`bulk-mail-row-${f.id}`}>
                    <input type="checkbox" checked={selected.has(f.id)} onChange={() => toggleOne(f.id)} className="w-3.5 h-3.5 accent-[#0020C2]" />
                    <span className="font-semibold flex-1 truncate">{f.name}</span>
                    <span className="text-neutral-400 truncate max-w-[220px]">{f.email}</span>
                    {(f.sent_emails || []).length > 0 && <span className="px-1.5 py-0.5 text-[9px] font-bold bg-[#F5F8FF] text-[#0020C2] whitespace-nowrap">{f.sent_emails.length} mail atıldı</span>}
                  </label>
                ))}
                {filtered.length === 0 && <div className="px-3 py-8 text-center text-xs text-neutral-400">Eşleşen firma yok (sadece mail adresi olan firmalar listelenir).</div>}
              </div>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-[10px] text-neutral-400">Mailler arka planda sırayla gönderilir (~1 sn/mail). Pencereden ilerlemeyi izleyebilirsiniz.</span>
              <button data-testid="bulk-mail-start" onClick={start} disabled={starting || selected.size === 0 || !templateId}
                className="flex items-center gap-2 px-5 py-2.5 text-[10px] uppercase tracking-widest font-bold bg-[#0020C2] text-white hover:bg-[#0A0A0A] disabled:opacity-50">
                {starting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                {selected.size > 0 ? `${selected.size} Firmaya Gönder` : "Gönder"}
              </button>
            </div>
          </div>
        )}

        {job && (
          <div className="p-5 space-y-4" data-testid="bulk-mail-progress">
            <div className="flex items-center justify-between text-sm font-bold">
              <span className="flex items-center gap-2">
                {running ? <Loader2 className="w-4 h-4 animate-spin text-[#0020C2]" /> :
                  job.status === "done" ? <CheckCircle2 className="w-4 h-4 text-[#16A34A]" /> : <StopCircle className="w-4 h-4 text-[#DC2626]" />}
                {running ? "Gönderiliyor..." : job.status === "done" ? "Tamamlandı" : "İptal edildi"}
              </span>
              <span className="num">{job.sent + job.failed} / {job.total}</span>
            </div>
            <div className="h-2.5 bg-[#F5F5F5] overflow-hidden">
              <div className="h-full bg-[#0020C2] transition-all duration-500" style={{ width: `${progress}%` }} />
            </div>
            <div className="flex items-center gap-4 text-xs">
              <span className="font-bold text-[#16A34A]" data-testid="bulk-mail-sent-count">{job.sent} başarılı</span>
              <span className={`font-bold ${job.failed ? "text-[#DC2626]" : "text-neutral-400"}`} data-testid="bulk-mail-failed-count">{job.failed} hatalı</span>
              {running && job.last_firm && <span className="text-neutral-400 truncate">Son: {job.last_firm}</span>}
            </div>
            {(job.errors || []).length > 0 && (
              <div className="border border-[#DC2626]/30 bg-[#DC2626]/5 max-h-40 overflow-y-auto">
                <div className="px-3 py-2 text-[10px] uppercase tracking-widest font-bold text-[#DC2626] flex items-center gap-1.5"><AlertTriangle className="w-3 h-3" /> Hatalar</div>
                {job.errors.map((er, i) => (
                  <div key={i} className="px-3 py-1 text-xs border-t border-[#DC2626]/10"><b>{er.firm}</b>: {er.error}</div>
                ))}
              </div>
            )}
            <div className="flex justify-end gap-2">
              {running ? (
                <button data-testid="bulk-mail-cancel" onClick={cancel} className="flex items-center gap-1.5 px-4 py-2 text-[10px] uppercase tracking-widest font-bold border border-[#DC2626] text-[#DC2626] hover:bg-[#DC2626] hover:text-white">
                  <StopCircle className="w-3.5 h-3.5" /> İptal Et
                </button>
              ) : (
                <button data-testid="bulk-mail-done-close" onClick={onClose} className="px-4 py-2 text-[10px] uppercase tracking-widest font-bold bg-[#0A0A0A] text-white hover:bg-[#0020C2]">Kapat</button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
